import React, { useState, useEffect, useMemo, useRef } from 'react';
import GameBoard from './GameBoard';
import WinnerScreen from './WinnerScreen';
import LandingPage from './LandingPage';
import StatsPage from './StatsPage';
import ForceUpdatePage from './ForceUpdatePage';
import AppPage from './AppPage';
import RoomSpectatorPage from './RoomSpectatorPage';
import allQuestions from './questions.json';
import generalEnglishQuestions from './questions-general-en.json';
import kidsArabicQuestions from './questions-kids-ar.json';
import kidsEnglishQuestions from './questions-kids-en.json';
import { trackWebEvent, trackWebSession } from './lib/tracking';
import { unlockSounds } from './sound';
import { getText } from './i18n';
import './landing.css';

const DEFAULT_SELECTED_CATEGORIES = ['مختلط'];
const DEFAULT_SELECTED_DIFFICULTIES = ['مختلط'];
const QUESTION_BANKS = [
  {
    key: 'general-ar',
    language: 'ar',
    questions: allQuestions,
    dir: 'rtl'
  },
  {
    key: 'general-en',
    language: 'en',
    questions: generalEnglishQuestions,
    dir: 'ltr'
  },
  {
    key: 'kids-ar',
    language: 'ar',
    questions: kidsArabicQuestions,
    dir: 'rtl'
  },
  {
    key: 'kids-en',
    language: 'en',
    questions: kidsEnglishQuestions,
    dir: 'ltr'
  }
];

function getCurrentPath() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const hashPath = window.location.hash.replace(/^#/, '').replace(/\/+$/, '');
  return hashPath || pathname;
}

function GameApp() {
  const [phase, setPhase] = useState('splash');
  const [teams, setTeams] = useState({
    team1: { name: '', color: 'red' },
    team2: { name: '', color: 'blue' }
  });
  const [winner, setWinner] = useState(null);
  const [initialTurn, setInitialTurn] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isOpenPlay, setIsOpenPlay] = useState(false);
  const [openPlaySeconds, setOpenPlaySeconds] = useState(10);
  const [isComputerMode, setIsComputerMode] = useState(false);
  const [selectedQuestionBankKey, setSelectedQuestionBankKey] = useState('general-ar');
  const [language, setLanguage] = useState('ar');
  const text = getText(language);

  const selectedQuestionBank = useMemo(() => {
    return QUESTION_BANKS.find(bank => bank.key === selectedQuestionBankKey) || QUESTION_BANKS[0];
  }, [selectedQuestionBankKey]);

  const questionBankOptions = useMemo(() => {
    return QUESTION_BANKS.filter(bank => bank.language === language).map(bank => ({
      ...bank,
      ...text.questionBanks[bank.key],
      disabled: false
    }));
  }, [language, text]);

  const selectedQuestionCount = useMemo(() => {
    return selectedQuestionBank.questions.filter(q => {
      const categoryOk = DEFAULT_SELECTED_CATEGORIES.includes('مختلط') || DEFAULT_SELECTED_CATEGORIES.includes(q.category);
      const difficultyOk = DEFAULT_SELECTED_DIFFICULTIES.includes('مختلط') || DEFAULT_SELECTED_DIFFICULTIES.includes(q.difficulty);
      return categoryOk && difficultyOk;
    }).length;
  }, [selectedQuestionBank]);

  const setInterfaceLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    setSelectedQuestionBankKey(nextLanguage === 'en' ? 'general-en' : 'general-ar');
  };

  useEffect(() => {
    const unlock = () => unlockSounds();
    const imagesToPreload = ['/mindclash-logo.png', '/saudi.png'];

    document.fonts?.load?.('32px "Segoe UI Emoji"', '👆').catch(() => {});
    document.fonts?.load?.('32px "Apple Color Emoji"', '👆').catch(() => {});

    imagesToPreload.forEach((src) => {
      const image = new Image();
      image.src = src;
    });

    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });

    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;
    let audio = null;
    let playPromise = null;

    if (phase === 'start' && !isMuted) {
      audio = new Audio('/start1.mp3');
      audio.loop = true;

      const tryPlay = () => {
        if (isCancelled) return;
        playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => console.log('Audio autoplay blocked by browser:', e));
        }
      };

      tryPlay();

      const onUserInteract = () => {
        if (isCancelled) return;
        tryPlay();
        cleanupListeners();
      };

      const cleanupListeners = () => {
        document.removeEventListener('click', onUserInteract);
        document.removeEventListener('keydown', onUserInteract);
        document.removeEventListener('touchstart', onUserInteract);
      };

      document.addEventListener('click', onUserInteract);
      document.addEventListener('keydown', onUserInteract);
      document.addEventListener('touchstart', onUserInteract);

      return () => {
        isCancelled = true;
        cleanupListeners();
        if (audio) {
          if (playPromise !== null && playPromise !== undefined) {
            playPromise.then(() => {
              audio.pause();
              audio.currentTime = 0;
            }).catch(() => {});
          } else {
            audio.pause();
            audio.currentTime = 0;
          }
        }
      };
    }
  }, [phase, isMuted]);

  const startGame = () => {
    unlockSounds();
    trackWebEvent('game_start', { mode: isComputerMode ? 'ai' : 'local', question_bank: selectedQuestionBank.key });
    setInitialTurn(Math.random() > 0.5 ? 1 : 2);
    setPhase('playing');
  };

  const toggleComputerMode = () => {
    setIsComputerMode(prev => {
      const next = !prev;
      if (next) {
        setTeams(current => ({
          ...current,
          team2: { ...current.team2, name: text.teamAiName }
        }));
      }
      return next;
    });
  };

  const selectGameMode = (mode) => {
    const nextIsComputerMode = mode === 'ai';
    setIsComputerMode(nextIsComputerMode);
    setTeams(current => {
      if (nextIsComputerMode) {
        return {
          ...current,
          team2: { ...current.team2, name: text.teamAiName }
        };
      }

      return {
        ...current,
        team2: {
          ...current.team2,
          name: current.team2.name === text.teamAiName ? '' : current.team2.name
        }
      };
    });
  };

  useEffect(() => {
    if (!isComputerMode) return;
    setTeams(current => {
      const aiNames = [getText('ar').teamAiName, getText('en').teamAiName];
      if (!aiNames.includes(current.team2.name)) return current;
      return {
        ...current,
        team2: { ...current.team2, name: text.teamAiName }
      };
    });
  }, [isComputerMode, text]);

  if (phase === 'splash') {
    return <SplashScreen onDone={() => setPhase('language')} />;
  }

  if (phase === 'language') {
    return <LanguageSelectScreen onSelect={(nextLanguage) => {
      setInterfaceLanguage(nextLanguage);
      setPhase('start');
    }} />;
  }

  if (phase === 'start') {
    return (
      <LandingPage
        teams={teams}
        setTeams={setTeams}
        selectedQuestionCount={selectedQuestionCount}
        questionBankOptions={questionBankOptions}
        selectedQuestionBankKey={selectedQuestionBank.key}
        setSelectedQuestionBankKey={setSelectedQuestionBankKey}
        language={language}
        text={text}
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        isOpenPlay={isOpenPlay}
        setIsOpenPlay={setIsOpenPlay}
        openPlaySeconds={openPlaySeconds}
        setOpenPlaySeconds={setOpenPlaySeconds}
        isComputerMode={isComputerMode}
        toggleComputerMode={toggleComputerMode}
        selectGameMode={selectGameMode}
        startGame={startGame}
      />
    );
  }

  if (phase === 'won') return <WinnerScreen winner={winner} onRestart={() => setPhase('start')} text={text} />;

  return (
    <div dir={text.dir} className="game-shell min-h-screen bg-wavy-animated px-3 py-4 md:p-4 flex flex-col items-center overflow-x-hidden">
      <span className="emoji-preload" aria-hidden="true">👆</span>
      <GameBoard
        teams={teams}
        initialTurn={initialTurn}
        selectedCategories={DEFAULT_SELECTED_CATEGORIES}
        selectedDifficulties={DEFAULT_SELECTED_DIFFICULTIES}
        questions={selectedQuestionBank.questions}
        isMuted={isMuted}
        isOpenPlay={isOpenPlay}
        openPlaySeconds={openPlaySeconds}
        isComputerMode={isComputerMode}
        text={text}
        onWin={(team) => {
          if (!team?.isDraw) {
            trackWebEvent('game_win', {
              mode: isComputerMode ? 'ai' : 'local',
              winner: isComputerMode
                ? team.color === 'red'
                  ? 'human'
                  : 'computer'
                : team.color === 'red'
                  ? 'team1'
                  : 'team2'
            });
          }
          setWinner(team);
          setPhase('won');
        }}
      />
    </div>
  );
}

function SplashScreen({ onDone }) {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    let frameId;
    let isDone = false;
    const start = performance.now();
    const duration = 2200;

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      setPercent(Math.round(progress * 100));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      if (!isDone) {
        isDone = true;
        window.setTimeout(onDone, 250);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      isDone = true;
      cancelAnimationFrame(frameId);
    };
  }, [onDone]);

  return (
    <main className="mobile-splash bilingual-splash" dir="rtl">
      <section className="mobile-splash-center">
        <img className="mobile-splash-logo" src="/mindclash-logo.png" alt="Knowledge Bridge" />
        <h1 className="mobile-splash-title">جسر المعرفة</h1>
        <p className="mobile-splash-subtitle">Knowledge Bridge</p>
        <div className="bilingual-splash-copy">
          <span>تحميل المحتوى</span>
          <span>Loading content</span>
        </div>
      </section>

      <footer className="mobile-splash-footer">
        <div className="mobile-splash-progress-track">
          <div className="mobile-splash-progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="mobile-splash-loading-row">
          <span>تحميل المحتوى / Loading content</span>
          <strong>{percent}%</strong>
        </div>
      </footer>
    </main>
  );
}

function LanguageSelectScreen({ onSelect }) {
  return (
    <main className="language-select-page">
      <section className="language-select-shell" aria-label="Choose the language">
        <img className="language-select-logo" src="/mindclash-logo.png" alt="Knowledge Bridge" />
        <div className="language-select-kicker">جسر المعرفة | Knowledge Bridge</div>
        <h1 className="language-select-title">
          <span dir="rtl">اختر اللغة</span>
          <span dir="ltr">Choose the language</span>
        </h1>
        <div className="language-select-actions">
          <button type="button" className="language-choice-card arabic" onClick={() => onSelect('ar')} dir="rtl">
            <span className="language-choice-main">عربي</span>
            <span className="language-choice-sub">الأسئلة والواجهة بالعربية</span>
          </button>
          <button type="button" className="language-choice-card english" onClick={() => onSelect('en')} dir="ltr">
            <span className="language-choice-main">English</span>
            <span className="language-choice-sub">English interface and questions</span>
          </button>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const [path, setPath] = useState(getCurrentPath);
  const trackedWebSessionRef = useRef(false);

  useEffect(() => {
    if (path === '/stats' || path === '/force-update' || trackedWebSessionRef.current) return;
    trackedWebSessionRef.current = true;
    trackWebSession();
  }, [path]);

  useEffect(() => {
    const handleLocation = () => {
      setPath(getCurrentPath());
    };

    window.addEventListener('popstate', handleLocation);
    window.addEventListener('hashchange', handleLocation);

    return () => {
      window.removeEventListener('popstate', handleLocation);
      window.removeEventListener('hashchange', handleLocation);
    };
  }, []);

  if (path === '/stats') {
    return <StatsPage />;
  }

  if (path === '/force-update') {
    return <ForceUpdatePage />;
  }

  if (path === '/app') {
    return <AppPage />;
  }

  if (path === '/room' || path.startsWith('/room/')) {
    const roomCode = path.split('/room/')[1]?.split('/')[0] || '';
    return <RoomSpectatorPage roomCode={roomCode} />;
  }

  return <GameApp />;
}
